import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const SOCIALS = [
  { key: 'social.github', icon: 'ri-github-line', href: 'https://github.com/AltusParati' },
  { key: 'social.instagram', icon: 'ri-instagram-line', href: 'https://instagram.com/gsoftwaree' },
  { key: 'social.x', icon: 'ri-twitter-x-line', href: 'https://x.com/GSoftwaree' },
  { key: 'social.tiktok', icon: 'ri-tiktok-line', href: 'https://www.tiktok.com/@gsoftwaree' },
  { key: 'social.email', icon: 'ri-mail-line', href: 'mailto:info@g-software.org' },
];

const BTN = 44;
const PERIM = BTN * 4;
const TAIL = 64;
const ITEM_PERIM = 400;
const ITEM_TAIL = 120;

export default function SocialDock() {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="fixed right-3 bottom-24 md:right-0 md:bottom-auto md:top-1/2 md:-translate-y-1/2 z-40 flex flex-col items-end">
      <div
        className={`flex flex-col gap-px mb-px transition-all duration-300 ${
          expanded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'
        }`}
      >
        {SOCIALS.map((s, i) => (
          <a
            key={s.key}
            href={s.href}
            target={s.href.startsWith('mailto') ? undefined : '_blank'}
            rel="noopener noreferrer"
            aria-label={t(s.key)}
            className="group relative overflow-hidden flex items-center gap-3 bg-[#111] border border-white/8 hover:border-white/25 hover:bg-[#1a1a1a] transition-all duration-200 cursor-pointer pr-4 pl-3 py-3"
            style={{ transitionDelay: expanded ? `${i * 40}ms` : '0ms' }}
          >
            <svg
              className="absolute inset-0 pointer-events-none z-10"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <rect
                x="0.5"
                y="0.5"
                width="99"
                height="99"
                fill="none"
                stroke="rgba(255,255,255,0.14)"
                strokeWidth="1"
              />
              <rect
                x="0.5"
                y="0.5"
                width="99"
                height="99"
                fill="none"
                stroke="rgba(255,255,255,0.88)"
                strokeWidth="1.6"
                strokeDasharray={`${ITEM_TAIL} ${ITEM_PERIM - ITEM_TAIL}`}
                strokeLinecap="round"
                style={{
                  strokeDashoffset: 0,
                  animation: `cometMoveItem 3.2s linear infinite`,
                  animationDelay: `${i * 0.2}s`,
                  willChange: 'stroke-dashoffset',
                }}
              />
            </svg>

            <div className="relative z-20 w-4 h-4 flex items-center justify-center">
              <i className={`${s.icon} text-neutral-500 group-hover:text-white transition-colors duration-200 text-sm`} />
            </div>
            <span className="relative z-20 font-mono text-[10px] text-neutral-400 group-hover:text-neutral-300 tracking-widest uppercase transition-colors duration-200 sm:whitespace-nowrap break-words max-w-[45vw] md:max-w-none">
              {t(s.key)}
            </span>
          </a>
        ))}
      </div>

      <div className="relative w-11 h-11">
        {!expanded && (
          <svg
            className="absolute inset-0 pointer-events-none z-10"
            style={{ width: BTN, height: BTN }}
            viewBox={`0 0 ${BTN} ${BTN}`}
          >
            <rect
              x="0.75"
              y="0.75"
              width={BTN - 1.5}
              height={BTN - 1.5}
              fill="none"
              stroke="rgba(255,255,255,0.16)"
              strokeWidth="1.2"
            />
            <rect
              x="0.75"
              y="0.75"
              width={BTN - 1.5}
              height={BTN - 1.5}
              fill="none"
              stroke="rgba(255,255,255,0.92)"
              strokeWidth="1.8"
              strokeDasharray={`${TAIL} ${PERIM - TAIL}`}
              strokeLinecap="round"
              style={{
                strokeDashoffset: 0,
                animation: `cometMoveTail 2.8s linear infinite`,
                willChange: 'stroke-dashoffset',
              }}
            />
          </svg>
        )}

        <button
          onClick={() => setExpanded((v) => !v)}
          aria-label={t('social.toggle')}
          className={`absolute inset-0 z-0 flex items-center justify-center cursor-pointer transition-all duration-300 ${
            expanded
              ? 'bg-white text-black border border-white'
              : 'bg-[#111] text-neutral-300 border border-transparent'
          }`}
        >
          <i className={`text-base transition-transform duration-300 ${
            expanded ? 'ri-close-line' : 'ri-share-line'
          }`} />
        </button>
      </div>

      <style>{`
        @keyframes cometMoveTail {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -${PERIM}; }
        }
        @keyframes cometMoveItem {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -${ITEM_PERIM}; }
        }
      `}</style>
    </div>
  );
}
